// src/components/common/Header.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User, Settings, ChevronDown } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

/* ── Color tokens ── */
const C = {
  amber:      '#d97706',
  amberLight: '#fef3c7',
  amberDark:  '#92400e',
  amberHover: '#b45309',
  white:      '#ffffff',
  gray100:    '#f3f4f6',
  gray200:    '#e5e7eb',
  gray400:    '#9ca3af',
  gray500:    '#6b7280',
  gray700:    '#374151',
  gray900:    '#111827',
};

const navItems = [
  { name: 'Home',             path: '/' },
  { name: 'Villa',            path: '/villas' },
  { name: 'Business Package', path: '/business' },
  { name: 'Services',         path: '/services' },
  { name: 'Profile',          path: '/profile' },
  { name: 'Contact Us',       path: '/contact' },
];

/* ── NavLink ── */
const NavLink = ({ to, children, active, onClick }) => {
  const [hov, setHov] = useState(false);
  return (
    <Link
      to={to}
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: 'relative', textDecoration: 'none',
        fontSize: 14, fontWeight: 500, fontFamily: 'inherit', whiteSpace: 'nowrap',
        color: active ? C.amber : (hov ? C.amber : C.gray700),
        transition: 'color 0.25s ease',
        paddingBottom: 4,
      }}
    >
      {children}
      {/* underline */}
      <span style={{
        position: 'absolute', bottom: -2, left: 0,
        height: 2, borderRadius: 2, backgroundColor: C.amber,
        width: active ? '100%' : (hov ? '100%' : '0%'),
        transition: 'width 0.3s cubic-bezier(0.34,1.56,0.64,1)',
      }} />
    </Link>
  );
};

/* ── LoginBtn ── */
const LoginBtn = () => {
  const [hov, setHov] = useState(false);
  return (
    <Link
      to="/admin/login"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '9px 22px', borderRadius: 10, textDecoration: 'none',
        background: hov
          ? `linear-gradient(135deg, ${C.amberHover}, #92400e)`
          : `linear-gradient(135deg, ${C.amber}, ${C.amberHover})`,
        color: C.white, fontSize: 13.5, fontWeight: 600, fontFamily: 'inherit',
        transform: hov ? 'translateY(-1px)' : 'translateY(0)',
        boxShadow: hov ? '0 8px 20px rgba(217,119,6,0.4)' : '0 4px 12px rgba(217,119,6,0.28)',
        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
      }}
    >
      Login
    </Link>
  );
};

/* ── MobileNavLink ── */
const MobileNavLink = ({ to, children, active, onClick }) => {
  const [hov, setHov] = useState(false);
  return (
    <Link
      to={to}
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'block', padding: '12px 16px', borderRadius: 10, textDecoration: 'none',
        fontSize: 15, fontWeight: active ? 600 : 500, fontFamily: 'inherit',
        color: active ? C.amber : (hov ? C.amber : C.gray700),
        backgroundColor: (active || hov) ? C.amberLight : 'transparent',
        transition: 'all 0.25s ease',
      }}
    >
      {children}
    </Link>
  );
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen]   = useState(false);
  const [scrolled, setScrolled]       = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, logout }              = useAuth();
  const location                      = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // close mobile menu on route change
  useEffect(() => { setIsMenuOpen(false); }, [location.pathname]);

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
    setUserMenuOpen(false);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');

        .header-root {
          position: sticky; top: 0; z-index: 1000;
          background: #ffffff;
          border-bottom: 1px solid #f3f4f6;
          transition: box-shadow 0.35s ease, background 0.35s ease;
          font-family: 'DM Sans', sans-serif;
        }
        .header-root.scrolled {
          box-shadow: 0 4px 24px rgba(0,0,0,0.08);
          background: rgba(255,255,255,0.97);
          backdrop-filter: blur(10px);
        }

        .user-dropdown {
          position: absolute; right: 0; top: calc(100% + 10px);
          background: #ffffff; border-radius: 14px;
          box-shadow: 0 20px 50px rgba(0,0,0,0.12);
          border: 1px solid #fef3c7; min-width: 180px;
          overflow: hidden; z-index: 200;
          animation: dropIn 0.25s cubic-bezier(0.34,1.56,0.64,1) both;
        }
        @keyframes dropIn {
          from { opacity: 0; transform: translateY(-10px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0)     scale(1);    }
        }

        .user-menu-item {
          display: flex; align-items: center; gap: 8px;
          width: 100%; padding: 12px 16px; border: none; background: transparent;
          font-size: 14px; font-weight: 500; font-family: 'DM Sans', sans-serif;
          color: #374151; text-decoration: none; cursor: pointer; text-align: left;
          transition: background 0.2s ease, color 0.2s ease;
        }
        .user-menu-item:hover { background: #fef3c7; color: #d97706; }

        .mobile-menu {
          background: #ffffff; border-top: 1px solid #fef3c7;
          overflow: hidden;
          animation: slideDown 0.3s cubic-bezier(0.34,1.56,0.64,1) both;
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .burger-btn {
          display: flex; align-items: center; justify-content: center;
          width: 40px; height: 40px; border-radius: 10px; border: none;
          background: transparent; cursor: pointer;
          color: #374151; transition: background 0.2s ease, color 0.2s ease;
        }
        .burger-btn:hover { background: #fef3c7; color: #d97706; }
      `}</style>

      <header className={`header-root${scrolled ? ' scrolled' : ''}`}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64 }}>

            {/* ── Logo ── */}
            <Link to="/" style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              <img src="/images/logo.png" alt="Dewanta Villa Management" style={{ height: 40, width: 'auto', objectFit: 'contain' }} />
            </Link>

            {/* ── Desktop Nav (center) ── */}
            <nav style={{
              position: 'absolute', left: '50%', transform: 'translateX(-50%)',
              display: 'flex', alignItems: 'center', gap: 28,
            }} className="desktop-nav">
              {navItems.map(item => (
                <NavLink key={item.path} to={item.path} active={location.pathname === item.path}>
                  {item.name}
                </NavLink>
              ))}
            </nav>

            {/* ── Right: user / login ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
              {user ? (
                <div style={{ position: 'relative' }}>
                  <UserBtn user={user} open={userMenuOpen} setOpen={setUserMenuOpen} />
                  {userMenuOpen && (
                    <div className="user-dropdown">
                      {user.role === 'admin' && (
                        <Link to="/admin/dashboard" className="user-menu-item" onClick={() => setUserMenuOpen(false)}>
                          <Settings size={15} /> Admin Panel
                        </Link>
                      )}
                      <button className="user-menu-item" onClick={handleLogout}>
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <LoginBtn />
              )}

              {/* Mobile burger */}
              <button className="burger-btn" onClick={() => setIsMenuOpen(v => !v)}
                style={{ display: 'none' }} id="mobile-burger">
                {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>

        {/* ── Mobile Nav ── */}
        {isMenuOpen && (
          <div className="mobile-menu">
            <div style={{ padding: '12px 24px 16px' }}>
              {navItems.map(item => (
                <MobileNavLink key={item.path} to={item.path}
                  active={location.pathname === item.path}
                  onClick={() => setIsMenuOpen(false)}>
                  {item.name}
                </MobileNavLink>
              ))}
              <div style={{ borderTop: `1px solid ${C.amberLight}`, marginTop: 12, paddingTop: 12 }}>
                {user ? (
                  <>
                    <div style={{ padding: '8px 16px', fontSize: 14, fontWeight: 600, color: C.amber, marginBottom: 4 }}>
                      Hi, {user.name}
                    </div>
                    {user.role === 'admin' && (
                      <MobileNavLink to="/admin/dashboard" onClick={() => setIsMenuOpen(false)}>
                        Admin Panel
                      </MobileNavLink>
                    )}
                    <button onClick={handleLogout} style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '12px 16px', borderRadius: 10, border: 'none',
                      background: 'transparent', fontSize: 15, fontWeight: 500,
                      color: C.gray700, cursor: 'pointer', fontFamily: 'inherit',
                    }}>
                      Logout
                    </button>
                  </>
                ) : (
                  <Link to="/admin/login" onClick={() => setIsMenuOpen(false)} style={{
                    display: 'block', padding: '12px 16px', borderRadius: 10, textDecoration: 'none',
                    fontSize: 15, fontWeight: 600, color: C.amber,
                    backgroundColor: C.amberLight, textAlign: 'center',
                  }}>
                    Login
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Responsive: hide desktop nav & user on mobile, show burger */}
        <style>{`
          @media (max-width: 768px) {
            .desktop-nav { display: none !important; }
            #mobile-burger { display: flex !important; }
          }
          @media (min-width: 769px) {
            #mobile-burger { display: none !important; }
          }
        `}</style>
      </header>
    </>
  );
};

/* ── UserBtn — local hover state ── */
const UserBtn = ({ user, open, setOpen }) => {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={() => setOpen(v => !v)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
        backgroundColor: (hov || open) ? C.amberLight : '#fafaf9',
        color: (hov || open) ? C.amber : '#374151',
        fontSize: 13.5, fontWeight: 600, fontFamily: 'inherit',
        transition: 'all 0.25s ease',
      }}
    >
      <User size={16} />
      {user.name}
      <ChevronDown size={14} style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.25s ease' }} />
    </button>
  );
};

export default Header;